/**
 * 💬 SPACE MESSAGING - Module Comms (Messagerie)
 * Centre de communication COMPLET style cockpit spatial
 * - Chat avec clients (Parle avec nous)
 * - Chat communautaire avec modération
 * - Support des photos
 * - Notifications sonores et visuelles
 * - Infos client détaillées (nom, photo, téléphone)
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MessageSquare, Send, Search, User, Clock, Check, CheckCheck,
  Image, Trash2, AlertTriangle, Ban, Shield, RefreshCw,
  Loader2, ChevronLeft, Star, Phone, Mail, Users, Store,
  Volume2, VolumeX, Bell, X, Maximize2, ImagePlus
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

// 🔔 Son de notification
const playNotificationSound = () => {
  try {
    const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioCtx.createOscillator();
    const gainNode = audioCtx.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioCtx.destination);
    
    oscillator.frequency.setValueAtTime(880, audioCtx.currentTime);
    oscillator.frequency.setValueAtTime(1100, audioCtx.currentTime + 0.1);
    oscillator.frequency.setValueAtTime(880, audioCtx.currentTime + 0.2);
    
    gainNode.gain.setValueAtTime(0.3, audioCtx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.3);
    
    oscillator.start(audioCtx.currentTime);
    oscillator.stop(audioCtx.currentTime + 0.3);
  } catch (e) {
    console.log('Audio not available');
  }
};

// 📸 Composant pour sélectionner et compresser une image
const ImageUploader = ({ onImageSelect, disabled }) => {
  const fileInputRef = useRef(null);
  
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Vérifier le type
    if (!file.type.startsWith('image/')) {
      toast.error('Seules les images sont autorisées');
      return;
    }
    
    // Vérifier la taille (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image trop lourde (max 5MB)');
      return;
    }
    
    // Compresser et convertir en base64
    const reader = new FileReader();
    reader.onload = async (event) => {
      const img = new window.Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const maxSize = 800;
        let width = img.width;
        let height = img.height;
        
        if (width > height && width > maxSize) {
          height = (height * maxSize) / width;
          width = maxSize;
        } else if (height > maxSize) {
          width = (width * maxSize) / height;
          height = maxSize;
        }
        
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        const compressedBase64 = canvas.toDataURL('image/jpeg', 0.8);
        onImageSelect(compressedBase64);
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        disabled={disabled}
        className="p-2 rounded-lg hover:bg-cyan-500/20 text-cyan-400 transition-colors disabled:opacity-50"
        title="Envoyer une photo"
      >
        <ImagePlus className="w-5 h-5" />
      </button>
    </>
  );
};

// 🖼️ Modal pour afficher une image en grand
const ImageModal = ({ imageUrl, onClose }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
    onClick={onClose}
  >
    <button
      onClick={onClose}
      className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
    >
      <X className="w-6 h-6" />
    </button>
    <img
      src={imageUrl}
      alt="Image agrandie"
      className="max-w-full max-h-full object-contain rounded-lg"
      onClick={(e) => e.stopPropagation()}
    />
  </motion.div>
);

// 📋 Liste des conversations
const ConversationList = ({ conversations, selectedId, onSelect, isLoading, activeTab, onTabChange, totalUnread }) => {
  const [search, setSearch] = useState('');

  const filtered = conversations.filter(conv => 
    conv.customerName?.toLowerCase().includes(search.toLowerCase()) ||
    conv.lastMessage?.message?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="h-full flex flex-col border-r border-cyan-500/20">
      {/* Header avec onglets */}
      <div className="p-4 border-b border-cyan-500/20">
        <h2 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-cyan-400" />
          Communications
          {totalUnread > 0 && (
            <span className="ml-auto bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full animate-pulse">
              {totalUnread}
            </span>
          )}
        </h2>
        
        {/* Onglets par canal */}
        <div className="flex gap-2 mb-3">
          <button
            onClick={() => onTabChange('team')}
            className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
              activeTab === 'team'
                ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:text-white'
            }`}
          >
            <Store className="w-4 h-4" />
            Clients
          </button>
          <button
            onClick={() => onTabChange('community')}
            className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
              activeTab === 'community'
                ? 'bg-gradient-to-r from-purple-500 to-pink-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:text-white'
            }`}
          >
            <Users className="w-4 h-4" />
            Communauté
          </button>
        </div>
        
        {/* Légende des sources */}
        {activeTab === 'team' && (
          <div className="flex items-center gap-3 mb-3 text-[10px] text-gray-500">
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-orange-500"></span>
              🌐 Widget = Chat bas de page
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-cyan-500"></span>
              👤 Client = Espace connecté
            </span>
          </div>
        )}
        
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher..."
            className="pl-9 bg-[#1a1a25] border-cyan-500/30 text-white text-sm"
          />
        </div>
      </div>

      {/* Liste */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-cyan-400 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-8 text-gray-500 text-sm">
            {activeTab === 'team' ? 'Aucune conversation' : 'Aucun message communautaire'}
          </div>
        ) : (
          filtered.map((conv) => (
            <motion.button
              key={conv.customerId}
              whileHover={{ backgroundColor: 'rgba(0, 212, 255, 0.05)' }}
              onClick={() => onSelect(conv)}
              className={`w-full p-4 text-left transition-colors border-b border-gray-800 ${
                selectedId === conv.customerId ? 'bg-cyan-500/10 border-l-2 border-l-cyan-500' : ''
              }`}
            >
              <div className="flex items-center gap-3">
                {/* Avatar avec photo client */}
                <div className="relative flex-shrink-0">
                  {conv.customerInfo?.profilePhoto ? (
                    <img 
                      src={conv.customerInfo.profilePhoto} 
                      alt={conv.customerName}
                      className="w-12 h-12 rounded-xl object-cover border-2 border-cyan-500/30"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white font-bold">
                      {conv.customerName?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                  )}
                  {conv.unread > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center rounded-full bg-red-500 text-white text-xs font-bold animate-pulse">
                      {conv.unread > 9 ? '9+' : conv.unread}
                    </span>
                  )}
                </div>

                {/* Infos */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <p className="text-white font-medium truncate">{conv.customerName}</p>
                      {/* Badge source : Widget vs Espace Client */}
                      {conv.lastMessage?.source === 'widget' ? (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-orange-500/20 text-orange-400 border border-orange-500/30" title="Message depuis le widget du site">
                          🌐 Site
                        </span>
                      ) : conv.lastMessage?.source === 'client_portal' ? (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-cyan-500/20 text-cyan-400 border border-cyan-500/30" title="Message depuis l'espace client">
                          👤 Client
                        </span>
                      ) : null}
                    </div>
                    <span className="text-xs text-gray-500">
                      {conv.lastMessageAt ? new Date(conv.lastMessageAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : ''}
                    </span>
                  </div>
                  <p className="text-sm text-gray-400 truncate">
                    {conv.lastMessage?.hasImage && '📷 '}
                    {conv.lastMessage?.message || 'Pas de message'}
                  </p>
                  {/* Téléphone client */}
                  {conv.customerInfo?.phone && (
                    <p className="text-xs text-cyan-400/60 mt-0.5">
                      📱 {conv.customerInfo.phone}
                    </p>
                  )}
                </div>
              </div>
            </motion.button>
          ))
        )}
      </div>
    </div>
  );
};

// 💬 Zone de chat
const ChatArea = ({ 
  conversation, 
  messages, 
  onSendMessage, 
  onDeleteMessage,
  onModerateMessage,
  isLoading, 
  activeTab,
  onRefresh 
}) => {
  const [newMessage, setNewMessage] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!newMessage.trim() && !selectedImage) return;
    
    setIsSending(true);
    try {
      await onSendMessage(newMessage.trim(), selectedImage);
      setNewMessage('');
      setSelectedImage(null);
    } finally {
      setIsSending(false);
    }
  };

  // Pour le mode communauté sans conversation sélectionnée
  if (activeTab === 'community' && !conversation) {
    return (
      <div className="h-full flex flex-col">
        {/* Header communauté */}
        <div className="p-4 border-b border-purple-500/20 flex items-center justify-between bg-gradient-to-r from-purple-900/30 to-pink-900/30">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
              <Users className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-white font-bold">Chat Communautaire</p>
              <p className="text-xs text-gray-400">Modération active</p>
            </div>
          </div>
          <button 
            onClick={onRefresh}
            className="p-2 rounded-lg hover:bg-purple-500/20 text-purple-400 transition-colors"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>

        {/* Messages communautaires */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-6 h-6 text-purple-400 animate-spin" />
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center text-gray-500 text-sm py-8">
              Aucun message dans la communauté
            </div>
          ) : (
            messages.map((msg, idx) => (
              <motion.div
                key={msg.id || idx}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="group"
              >
                <div className="flex items-start gap-3">
                  {/* Avatar */}
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                    {msg.senderName?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-purple-300 font-medium text-sm">{msg.senderName}</span>
                      <span className="text-xs text-gray-500">
                        {new Date(msg.createdAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    
                    {/* Image si présente */}
                    {msg.imageBase64 && (
                      <div 
                        className="mb-2 cursor-pointer"
                        onClick={() => setPreviewImage(msg.imageBase64)}
                      >
                        <img 
                          src={msg.imageBase64} 
                          alt="Photo partagée"
                          className="max-w-xs rounded-lg border border-purple-500/30 hover:border-purple-400 transition-colors"
                        />
                      </div>
                    )}
                    
                    <div className="bg-[#1a1a25] rounded-xl rounded-tl-sm p-3 border border-purple-500/20 inline-block">
                      <p className="text-white text-sm">{msg.message}</p>
                    </div>
                  </div>
                  
                  {/* Actions de modération */}
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                    <button
                      onClick={() => onDeleteMessage(msg.id)}
                      className="p-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/40 text-red-400 transition-colors"
                      title="Supprimer"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => onModerateMessage(msg.id, 'warn')}
                      className="p-1.5 rounded-lg bg-yellow-500/20 hover:bg-yellow-500/40 text-yellow-400 transition-colors"
                      title="Avertir"
                    >
                      <AlertTriangle className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => onModerateMessage(msg.id, 'ban')}
                      className="p-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/40 text-red-400 transition-colors"
                      title="Bannir"
                    >
                      <Ban className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>
    );
  }

  // Mode "Parle avec nous" sans conversation sélectionnée
  if (!conversation) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <MessageSquare className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-500">Sélectionnez une conversation</p>
          <p className="text-gray-600 text-sm mt-2">pour répondre aux clients</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header conversation avec infos client */}
      <div className="p-4 border-b border-cyan-500/20 flex items-center justify-between bg-gradient-to-r from-cyan-900/20 to-blue-900/20">
        <div className="flex items-center gap-3">
          {/* Photo client */}
          {conversation.customerInfo?.profilePhoto ? (
            <img 
              src={conversation.customerInfo.profilePhoto} 
              alt={conversation.customerName}
              className="w-12 h-12 rounded-xl object-cover border-2 border-cyan-500/50"
            />
          ) : (
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white font-bold text-lg">
              {conversation.customerName?.charAt(0)?.toUpperCase() || '?'}
            </div>
          )}
          
          <div>
            <p className="text-white font-bold">{conversation.customerName}</p>
            <div className="flex items-center gap-3 text-xs text-gray-400">
              {conversation.customerInfo?.phone && (
                <span className="flex items-center gap-1">
                  <Phone className="w-3 h-3 text-cyan-400" />
                  {conversation.customerInfo.phone}
                </span>
              )}
              {conversation.customerInfo?.email && (
                <span className="flex items-center gap-1">
                  <Mail className="w-3 h-3 text-pink-400" />
                  {conversation.customerInfo.email}
                </span>
              )}
            </div>
          </div>
        </div>
        
        <button 
          onClick={onRefresh}
          className="p-2 rounded-lg hover:bg-cyan-500/20 text-cyan-400 transition-colors"
        >
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-6 h-6 text-cyan-400 animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center text-gray-500 text-sm py-8">
            Démarrez la conversation !
          </div>
        ) : (
          [...messages].reverse().map((msg, idx) => (
            <motion.div
              key={msg.id || idx}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex ${msg.isTeamMessage ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[70%] p-3 rounded-2xl ${
                  msg.isTeamMessage
                    ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-br-sm'
                    : 'bg-[#1a1a25] text-white border border-gray-700 rounded-bl-sm'
                }`}
              >
                {/* Badge source pour les messages clients */}
                {!msg.isTeamMessage && msg.source && (
                  <div className="mb-1.5">
                    {msg.source === 'widget' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-orange-500/20 text-orange-300 border border-orange-500/30">
                        🌐 Widget Site
                      </span>
                    ) : msg.source === 'client_portal' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-cyan-500/20 text-cyan-300 border border-cyan-500/30">
                        👤 Espace Client
                      </span>
                    ) : null}
                  </div>
                )}
                
                {/* Image si présente */}
                {msg.imageBase64 && (
                  <div 
                    className="mb-2 cursor-pointer"
                    onClick={() => setPreviewImage(msg.imageBase64)}
                  >
                    <img 
                      src={msg.imageBase64} 
                      alt="Photo partagée"
                      className="max-w-full rounded-lg hover:opacity-90 transition-opacity"
                    />
                  </div>
                )}
                
                <p className="text-sm">{msg.message}</p>
                <div className={`flex items-center justify-end gap-1 mt-1 ${msg.isTeamMessage ? 'text-cyan-200' : 'text-gray-500'}`}>
                  <span className="text-xs">
                    {new Date(msg.createdAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                  {msg.isTeamMessage && (
                    msg.readByCustomer ? <CheckCheck className="w-3 h-3" /> : <Check className="w-3 h-3" />
                  )}
                </div>
              </div>
            </motion.div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Prévisualisation image sélectionnée */}
      {selectedImage && (
        <div className="px-4 py-2 border-t border-cyan-500/20 bg-[#0a0a0f]">
          <div className="flex items-center gap-2">
            <img 
              src={selectedImage} 
              alt="Aperçu" 
              className="w-16 h-16 object-cover rounded-lg border border-cyan-500/30"
            />
            <div className="flex-1 text-sm text-gray-400">Photo prête à envoyer</div>
            <button
              onClick={() => setSelectedImage(null)}
              className="p-1 rounded-lg hover:bg-red-500/20 text-red-400"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Input */}
      <div className="p-4 border-t border-cyan-500/20 bg-[#0a0a0f]">
        <div className="flex items-center gap-3">
          <ImageUploader onImageSelect={setSelectedImage} disabled={isSending} />
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
            placeholder="Tapez votre message..."
            className="flex-1 bg-[#1a1a25] border-cyan-500/30 text-white"
            disabled={isSending}
          />
          <Button
            onClick={handleSend}
            disabled={(!newMessage.trim() && !selectedImage) || isSending}
            className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500"
          >
            {isSending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
          </Button>
        </div>
      </div>
      
      {/* Modal image */}
      <AnimatePresence>
        {previewImage && (
          <ImageModal imageUrl={previewImage} onClose={() => setPreviewImage(null)} />
        )}
      </AnimatePresence>
    </div>
  );
};

// 🚀 Module Messagerie principal
const SpaceMessaging = () => {
  const [conversations, setConversations] = useState([]);
  const [communityMessages, setCommunityMessages] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('team'); // 'team' ou 'community'
  const [totalUnread, setTotalUnread] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [lastUnreadCount, setLastUnreadCount] = useState(0);

  // Charger les conversations
  const loadConversations = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/chat/admin/messages?channel=team`);
      const data = await response.json();
      setConversations(data.conversations || []);
      
      const newUnread = data.totalUnread || 0;
      
      // Jouer le son si nouveaux messages
      if (newUnread > lastUnreadCount && soundEnabled && lastUnreadCount !== 0) {
        playNotificationSound();
        toast.info('🔔 Nouveau message !', { duration: 2000 });
      }
      setLastUnreadCount(newUnread);
      setTotalUnread(newUnread);
    } catch (error) {
      console.error('Erreur chargement conversations:', error);
    } finally {
      setIsLoading(false);
    }
  }, [lastUnreadCount, soundEnabled]);

  // Charger les messages communautaires
  const loadCommunityMessages = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/chat/admin/messages?channel=community`);
      const data = await response.json();
      
      // Aplatir tous les messages communautaires
      const allMessages = [];
      (data.conversations || []).forEach(conv => {
        allMessages.push(...(conv.messages || []));
      });
      
      // Trier par date
      allMessages.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      setCommunityMessages(allMessages);
    } catch (error) {
      console.error('Erreur chargement messages communautaires:', error);
    }
  }, []);

  // Charger les messages d'une conversation
  const loadMessages = async (customerId) => {
    setMessagesLoading(true);
    try {
      const conv = conversations.find(c => c.customerId === customerId);
      setMessages(conv?.messages || []);
      
      // Marquer comme lu
      await fetch(`${API}/api/chat/admin/read/${customerId}`, { method: 'POST' });
      
      // Rafraîchir les conversations pour mettre à jour le compteur
      loadConversations();
    } catch (error) {
      console.error('Erreur chargement messages:', error);
    } finally {
      setMessagesLoading(false);
    }
  };

  // Polling pour les nouvelles conversations
  useEffect(() => {
    loadConversations();
    const interval = setInterval(loadConversations, 10000); // Toutes les 10 secondes
    return () => clearInterval(interval);
  }, [loadConversations]);

  // Charger selon l'onglet actif
  useEffect(() => {
    if (activeTab === 'community') {
      loadCommunityMessages();
    }
  }, [activeTab, loadCommunityMessages]);

  // Charger les messages quand on sélectionne une conversation
  useEffect(() => {
    if (selectedConversation) {
      loadMessages(selectedConversation.customerId);
    }
  }, [selectedConversation]);

  // Envoyer un message
  const handleSendMessage = async (content, imageBase64 = null) => {
    if (!selectedConversation && activeTab === 'team') return;
    if (!content && !imageBase64) return;

    try {
      const body = {
        message: content || '',
        channel: activeTab,
        replyToCustomerId: selectedConversation?.customerId || null
      };
      
      if (imageBase64) {
        body.imageBase64 = imageBase64;
      }
      
      const response = await fetch(`${API}/api/chat/admin/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      
      if (response.ok) {
        toast.success('Message envoyé !');
        
        if (activeTab === 'team') {
          loadMessages(selectedConversation.customerId);
        } else {
          loadCommunityMessages();
        }
      } else {
        throw new Error('Erreur envoi');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    }
  };

  // Supprimer un message
  const handleDeleteMessage = async (messageId) => {
    if (!confirm('Supprimer ce message ?')) return;
    
    try {
      const response = await fetch(`${API}/api/chat/admin/messages/${messageId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        toast.success('Message supprimé');
        loadCommunityMessages();
      }
    } catch (error) {
      toast.error('Erreur suppression');
    }
  };

  // Modérer un message
  const handleModerateMessage = async (messageId, action) => {
    const reason = action === 'ban' 
      ? prompt('Raison du bannissement :') 
      : action === 'warn' 
        ? prompt('Raison de l\'avertissement :')
        : null;
    
    if ((action === 'ban' || action === 'warn') && reason === null) return;
    
    try {
      const response = await fetch(`${API}/api/chat/admin/moderate/${messageId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, reason })
      });
      
      if (response.ok) {
        const actionLabels = { delete: 'supprimé', warn: 'averti', ban: 'banni' };
        toast.success(`Utilisateur ${actionLabels[action]} !`);
        loadCommunityMessages();
      }
    } catch (error) {
      toast.error('Erreur modération');
    }
  };

  const handleRefresh = () => {
    if (activeTab === 'team') {
      loadConversations();
      if (selectedConversation) {
        loadMessages(selectedConversation.customerId);
      }
    } else {
      loadCommunityMessages();
    }
    toast.success('Actualisation...');
  };

  return (
    <div className="h-[calc(100vh-120px)] flex relative">
      {/* Bouton son */}
      <button
        onClick={() => setSoundEnabled(!soundEnabled)}
        className={`absolute top-2 right-2 z-10 p-2 rounded-lg transition-colors ${
          soundEnabled ? 'bg-cyan-500/20 text-cyan-400' : 'bg-gray-800 text-gray-500'
        }`}
        title={soundEnabled ? 'Désactiver les sons' : 'Activer les sons'}
      >
        {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
      </button>
      
      {/* Liste des conversations */}
      <div className="w-80 flex-shrink-0">
        <ConversationList
          conversations={activeTab === 'team' ? conversations : communityMessages.length > 0 ? [{ customerId: 'community', customerName: 'Communauté', messages: communityMessages }] : []}
          selectedId={selectedConversation?.customerId}
          onSelect={setSelectedConversation}
          isLoading={isLoading}
          activeTab={activeTab}
          onTabChange={(tab) => {
            setActiveTab(tab);
            setSelectedConversation(null);
          }}
          totalUnread={totalUnread}
        />
      </div>

      {/* Zone de chat */}
      <div className="flex-1 bg-[#0a0a0f]">
        <ChatArea
          conversation={selectedConversation}
          messages={activeTab === 'team' ? messages : communityMessages}
          onSendMessage={handleSendMessage}
          onDeleteMessage={handleDeleteMessage}
          onModerateMessage={handleModerateMessage}
          isLoading={messagesLoading}
          activeTab={activeTab}
          onRefresh={handleRefresh}
        />
      </div>
    </div>
  );
};

export default SpaceMessaging;
